
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  BookOpen, 
  Play, 
  CheckCircle, 
  Clock, 
  Users, 
  Award,
  Star,
  Target,
  Calendar,
  TrendingUp
} from 'lucide-react';

const AcademyDashboard = () => {
  const [activeModule, setActiveModule] = useState('fundamentals');

  const learningPaths = [
    {
      id: 'fundamentals',
      title: 'JUV Fundamentals',
      description: 'Core principles and brand values',
      progress: 75,
      totalLessons: 8,
      completedLessons: 6,
      estimatedTime: '2 hours',
      difficulty: 'Beginner',
      category: 'Foundation'
    },
    {
      id: 'aesthetics',
      title: 'Aesthetic Treatments',
      description: 'Advanced treatment protocols',
      progress: 45,
      totalLessons: 12,
      completedLessons: 5,
      estimatedTime: '4 hours',
      difficulty: 'Intermediate',
      category: 'Clinical'
    },
    {
      id: 'customer-care',
      title: 'Customer Care Excellence',
      description: 'Patient experience optimization',
      progress: 90,
      totalLessons: 6,
      completedLessons: 5,
      estimatedTime: '1.5 hours',
      difficulty: 'Beginner',
      category: 'Service'
    }
  ];

  const recentActivity = [
    {
      type: 'completion',
      title: 'Completed: Botox Safety Protocols',
      timestamp: '2 hours ago',
      points: 50
    },
    {
      type: 'quiz',
      title: 'Passed Quiz: Customer Consultation',
      timestamp: '1 day ago',
      points: 25
    },
    {
      type: 'certificate',
      title: 'Earned Certificate: JUV Brand Standards',
      timestamp: '3 days ago',
      points: 100
    }
  ];

  const upcomingDeadlines = [
    {
      title: 'Monthly Compliance Training',
      dueDate: '2024-01-25',
      priority: 'high'
    },
    {
      title: 'Product Knowledge Update',
      dueDate: '2024-01-30',
      priority: 'medium'
    }
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-juv-soft-blush text-juv-deep-chestnut';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-juv-soft-blush text-juv-deep-chestnut';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-juv-cream to-juv-sage/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold font-juv-heading text-juv-deep-chestnut">
                JUV Academy Dashboard
              </h1>
              <p className="mt-1 text-juv-deep-chestnut/70">
                Continue your learning journey
              </p>
            </div>
            <Button className="bg-juv-terracotta text-white">
              <BookOpen className="h-4 w-4 mr-2" />
              Browse Catalog
            </Button>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                    <BookOpen className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Courses in Progress</p>
                    <p className="text-2xl font-bold text-juv-deep-chestnut">3</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Completed</p>
                    <p className="text-2xl font-bold text-juv-deep-chestnut">12</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-juv-soft-blush flex items-center justify-center mr-4">
                    <Star className="h-6 w-6 text-juv-terracotta" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Points</p>
                    <p className="text-2xl font-bold text-juv-deep-chestnut">1,250</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mr-4">
                    <Award className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Certificates</p>
                    <p className="text-2xl font-bold text-juv-deep-chestnut">4</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Current Learning Paths */}
            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardHeader>
                <CardTitle className="font-juv-heading text-juv-deep-chestnut">
                  Current Learning Paths
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {learningPaths.map((path) => (
                    <div 
                      key={path.id}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        activeModule === path.id ? 
                        'border-juv-terracotta bg-juv-soft-blush/30' : 
                        'border-juv-rose-clay/20 hover:border-juv-rose-clay/40'
                      }`}
                      onClick={() => setActiveModule(path.id)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-juv-deep-chestnut">{path.title}</h4>
                          <p className="text-sm text-juv-deep-chestnut/70">{path.description}</p>
                        </div>
                        <Badge className={getDifficultyColor(path.difficulty)}>
                          {path.difficulty}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-juv-deep-chestnut/70">Progress</span>
                          <span className="font-medium text-juv-deep-chestnut">{path.progress}%</span>
                        </div>
                        <Progress value={path.progress} className="h-2" />
                        <div className="flex justify-between text-xs text-juv-deep-chestnut/70">
                          <span>{path.completedLessons}/{path.totalLessons} lessons</span>
                          <span>{path.estimatedTime} remaining</span>
                        </div>
                      </div>
                      
                      <Button 
                        size="sm" 
                        className="w-full mt-3 bg-juv-terracotta text-white"
                      >
                        Continue Learning
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity & Deadlines */}
            <div className="space-y-6">
              {/* Recent Activity */}
              <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
                <CardHeader>
                  <CardTitle className="font-juv-heading text-juv-deep-chestnut">
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {recentActivity.map((activity, index) => (
                      <div key={index} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-juv-soft-blush/10">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          activity.type === 'completion' ? 'bg-green-100' :
                          activity.type === 'quiz' ? 'bg-blue-100' :
                          'bg-purple-100'
                        }`}>
                          {activity.type === 'completion' && <CheckCircle className="h-4 w-4 text-green-600" />}
                          {activity.type === 'quiz' && <Target className="h-4 w-4 text-blue-600" />}
                          {activity.type === 'certificate' && <Award className="h-4 w-4 text-purple-600" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-juv-deep-chestnut">{activity.title}</p>
                          <p className="text-xs text-juv-deep-chestnut/70">{activity.timestamp}</p>
                        </div>
                        <Badge className="bg-juv-soft-blush text-juv-deep-chestnut">
                          +{activity.points} pts
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Upcoming Deadlines */}
              <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
                <CardHeader>
                  <CardTitle className="font-juv-heading text-juv-deep-chestnut flex items-center">
                    <Calendar className="h-5 w-5 mr-2" />
                    Upcoming Deadlines
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {upcomingDeadlines.map((deadline, index) => (
                      <div key={index} className="flex items-center justify-between p-3 rounded-lg border border-juv-rose-clay/20">
                        <div>
                          <p className="font-medium text-juv-deep-chestnut">{deadline.title}</p>
                          <p className="text-sm text-juv-deep-chestnut/70">
                            Due: {new Date(deadline.dueDate).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge className={getPriorityColor(deadline.priority)}>
                          {deadline.priority}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcademyDashboard;
